/* global axios */
import ApiClient from './ApiClient';

class CrmDealsAPI extends ApiClient {
  constructor() {
    super('crm/deals', { accountScoped: true });
  }

  // ApiClient#get() takes no arguments, but the deals index needs to be
  // filterable (funnel_id, stage_id, status, assignee_id, q, per_page).
  // Overriding here so callers can pass params through as query string.
  get(params) {
    return axios.get(this.url, { params });
  }

  board(params) {
    return axios.get(`${this.baseUrl()}/crm/board`, { params });
  }

  // Modo conversational: a coluna é etiqueta, e a soma/contagem vêm agregadas
  // do servidor pelo mesmo motivo do board — a lista de cards é paginada.
  labelTotals(params) {
    return axios.get(`${this.baseUrl()}/crm/board/label_totals`, { params });
  }

  // Cards do modo conversational: conversas das etiquetas do funil num
  // formato enxuto, paginado de 500 em 500 (ver Crm::BoardCardsFinder).
  boardCards(params) {
    return axios.get(`${this.baseUrl()}/crm/board/cards`, { params });
  }

  funnels() {
    return axios.get(`${this.baseUrl()}/crm/funnels`);
  }

  createFunnel(funnel) {
    return axios.post(`${this.baseUrl()}/crm/funnels`, { funnel });
  }

  updateFunnel(id, funnel) {
    return axios.patch(`${this.baseUrl()}/crm/funnels/${id}`, { funnel });
  }

  deleteFunnel(id) {
    return axios.delete(`${this.baseUrl()}/crm/funnels/${id}`);
  }

  createStage(funnelId, stage) {
    return axios.post(`${this.baseUrl()}/crm/funnels/${funnelId}/stages`, {
      stage,
    });
  }

  // Campos que uma etapa pode exigir. Vem do servidor pra tela e validação não
  // divergirem.
  requirementFields(funnelId) {
    return axios.get(
      `${this.baseUrl()}/crm/funnels/${funnelId}/stages/requirement_fields`
    );
  }

  // scope 'win' marca o checklist do ganho (lista do funil), sem etapa.
  updateChecklistItem(dealId, item, checked, scope = 'stage') {
    return axios.patch(`${this.url}/${dealId}/checklist`, {
      item,
      checked,
      scope,
    });
  }

  listDocuments(dealId) {
    return axios.get(`${this.url}/${dealId}/documents`);
  }

  uploadDocument(dealId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${this.url}/${dealId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  deleteDocument(dealId, documentId) {
    return axios.delete(`${this.url}/${dealId}/documents/${documentId}`);
  }

  updateStage(funnelId, stageId, stage) {
    return axios.patch(
      `${this.baseUrl()}/crm/funnels/${funnelId}/stages/${stageId}`,
      { stage }
    );
  }

  deleteStage(funnelId, stageId) {
    return axios.delete(
      `${this.baseUrl()}/crm/funnels/${funnelId}/stages/${stageId}`
    );
  }

  moveToStage(id, stageId) {
    return axios.patch(`${this.url}/${id}/stage`, { stage_id: stageId });
  }

  createDeal(deal) {
    return axios.post(this.url, { deal });
  }

  getDeal(id) {
    return axios.get(`${this.url}/${id}`);
  }

  updateDeal(id, deal) {
    return axios.patch(`${this.url}/${id}`, { deal });
  }

  origins() {
    return axios.get(`${this.baseUrl()}/crm/origins`);
  }

  createOrigin(origin) {
    return axios.post(`${this.baseUrl()}/crm/origins`, { origin });
  }

  updateOrigin(id, origin) {
    return axios.patch(`${this.baseUrl()}/crm/origins/${id}`, { origin });
  }

  deleteOrigin(id) {
    return axios.delete(`${this.baseUrl()}/crm/origins/${id}`);
  }

  paymentMethods() {
    return axios.get(`${this.baseUrl()}/crm/payment_methods`);
  }

  createPaymentMethod(paymentMethod) {
    return axios.post(`${this.baseUrl()}/crm/payment_methods`, {
      payment_method: paymentMethod,
    });
  }

  updatePaymentMethod(id, paymentMethod) {
    return axios.patch(`${this.baseUrl()}/crm/payment_methods/${id}`, {
      payment_method: paymentMethod,
    });
  }

  deletePaymentMethod(id) {
    return axios.delete(`${this.baseUrl()}/crm/payment_methods/${id}`);
  }

  lostReasons() {
    return axios.get(`${this.baseUrl()}/crm/lost_reasons`);
  }

  createLostReason(lostReason) {
    return axios.post(`${this.baseUrl()}/crm/lost_reasons`, {
      lost_reason: lostReason,
    });
  }

  updateLostReason(id, lostReason) {
    return axios.patch(`${this.baseUrl()}/crm/lost_reasons/${id}`, {
      lost_reason: lostReason,
    });
  }

  deleteLostReason(id) {
    return axios.delete(`${this.baseUrl()}/crm/lost_reasons/${id}`);
  }

  products() {
    return axios.get(`${this.baseUrl()}/crm/products`);
  }

  createProduct(product) {
    return axios.post(`${this.baseUrl()}/crm/products`, { product });
  }

  updateProduct(id, product) {
    return axios.patch(`${this.baseUrl()}/crm/products/${id}`, { product });
  }

  deleteProduct(id) {
    return axios.delete(`${this.baseUrl()}/crm/products/${id}`);
  }

  tasks(params) {
    return axios.get(`${this.baseUrl()}/crm/tasks`, { params });
  }

  // Pendências do ícone da barra lateral: novas, atrasadas, hoje, próximas.
  taskPendencies() {
    return axios.get(`${this.baseUrl()}/crm/tasks/pendencies`);
  }

  // Abrir o painel marca as "novas" como vistas e devolve a lista de novo.
  markTaskPendenciesSeen() {
    return axios.post(`${this.baseUrl()}/crm/tasks/seen`);
  }

  // Tarefa avulsa da agenda: sem negócio atrás, por isso não usa a rota
  // aninhada em /crm/deals/:id/tasks.
  createStandaloneTask(task) {
    return axios.post(`${this.baseUrl()}/crm/tasks`, { task });
  }

  updateStandaloneTask(id, task) {
    return axios.patch(`${this.baseUrl()}/crm/tasks/${id}`, { task });
  }

  deleteStandaloneTask(id) {
    return axios.delete(`${this.baseUrl()}/crm/tasks/${id}`);
  }

  createTask(dealId, task) {
    return axios.post(`${this.url}/${dealId}/tasks`, { task });
  }

  updateTask(dealId, taskId, task) {
    return axios.patch(`${this.url}/${dealId}/tasks/${taskId}`, { task });
  }

  deleteTask(dealId, taskId) {
    return axios.delete(`${this.url}/${dealId}/tasks/${taskId}`);
  }

  generateInstallments(dealId, payload) {
    return axios.post(`${this.url}/${dealId}/installments/generate`, payload);
  }

  updateInstallment(dealId, installmentId, installment) {
    return axios.patch(`${this.url}/${dealId}/installments/${installmentId}`, {
      installment,
    });
  }

  deleteInstallment(dealId, installmentId) {
    return axios.delete(`${this.url}/${dealId}/installments/${installmentId}`);
  }

  addProductLine(dealId, productLine) {
    return axios.post(`${this.url}/${dealId}/product_lines`, {
      product_line: productLine,
    });
  }

  removeProductLine(dealId, lineId) {
    return axios.delete(`${this.url}/${dealId}/product_lines/${lineId}`);
  }
}

export default new CrmDealsAPI();
